import React, { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api';

// Sezione "Guida valori ammessi" riutilizzabile dalle pagine con import Excel.
// Le classi CSS `listini-guida-*` sono globali (bop-design-system.css) e nate
// con la pagina Listini fornitori: qui vengono riusate senza duplicarle.

// ── Tipi ──────────────────────────────────────────────────────────────────────

export interface ValoriAmmessi {
  tipologie: { valore: string; label: string }[];
  unitaMisura: { id: number; simbolo: string; nome: string }[];
  marche: { id: number; nome: string }[];
  collezioni: { id: number; nome: string; marcaId: number }[];
  formati: { id: number; nome: string }[];
  categorieMerceologiche: { id: number; nome: string }[];
  categorieConfigurazione: { id: number; nome: string }[];
  effetti: { id: number; nome: string }[];
}

export interface GuidaCampo {
  nome: string;
  colonnaExcel: string;
  tipo: 'ENUM' | 'TABELLATO';
  valori: string[];
  descrizioni?: Record<string, string>;
  nota?: string;
}

interface GuidaValoriAmmessiProps {
  societaId: string;
  /** Estrae i campi da mostrare dai valori ammessi della società. */
  campi: (valori: ValoriAmmessi) => GuidaCampo[];
  intro?: React.ReactNode;
}

const GUIDA_SHOW_LIMIT = 10;

// ── Chip copiabile ────────────────────────────────────────────────────────────

function GuidaChip({ valore, descrizione }: { valore: string; descrizione?: string }) {
  const [copiato, setCopiato] = useState(false);
  const handleClick = () => {
    navigator.clipboard.writeText(valore).catch(() => {});
    setCopiato(true);
    setTimeout(() => setCopiato(false), 1400);
  };
  return (
    <button
      className={`listini-guida-chip${copiato ? ' listini-guida-chip--copiato' : ''}`}
      onClick={handleClick}
      title={descrizione || valore}
    >
      {copiato ? '✓ copiato' : valore}
    </button>
  );
}

// ── Card di un singolo campo ──────────────────────────────────────────────────

function GuidaFieldCard({ nome, colonnaExcel, tipo, valori, descrizioni, nota }: GuidaCampo) {
  const [espansa, setEspansa] = useState(false);
  const [cerca, setCerca] = useState('');

  const valoriFiltrati = cerca
    ? valori.filter(v => v.toLowerCase().includes(cerca.toLowerCase()))
    : valori;

  const visibili = espansa ? valoriFiltrati : valoriFiltrati.slice(0, GUIDA_SHOW_LIMIT);
  const nascosti = valoriFiltrati.length - GUIDA_SHOW_LIMIT;

  return (
    <div className="listini-guida-field-card">
      <div className="listini-guida-field-header">
        <div className="listini-guida-field-header-left">
          <span className="listini-guida-field-nome">{nome}</span>
          <code className="listini-guida-field-col">{colonnaExcel}</code>
        </div>
        <span className={`bop-badge ${tipo === 'ENUM' ? 'bop-badge-info' : 'bop-badge-warning'}`}>
          {tipo}
        </span>
      </div>
      <div className="listini-guida-field-body">
        {nota && <p className="listini-guida-field-nota">{nota}</p>}
        {valori.length > GUIDA_SHOW_LIMIT && (
          <input
            type="text"
            className="bop-input listini-guida-search"
            placeholder="Cerca..."
            value={cerca}
            onChange={e => { setCerca(e.target.value); setEspansa(false); }}
          />
        )}
        <div className="listini-guida-chip-list">
          {visibili.map(v => (
            <GuidaChip key={v} valore={v} descrizione={descrizioni?.[v]} />
          ))}
          {valoriFiltrati.length === 0 && (
            <span className="listini-guida-empty">Nessun valore disponibile</span>
          )}
        </div>
        {!espansa && nascosti > 0 && (
          <button className="listini-guida-show-more" onClick={() => setEspansa(true)}>
            Mostra altri {nascosti} ▼
          </button>
        )}
        {espansa && valoriFiltrati.length > GUIDA_SHOW_LIMIT && (
          <button className="listini-guida-show-more" onClick={() => setEspansa(false)}>
            Mostra meno ▲
          </button>
        )}
        <div className="listini-guida-copy-hint">Clicca per copiare</div>
      </div>
    </div>
  );
}

// ── Sezione completa ──────────────────────────────────────────────────────────

export default function GuidaValoriAmmessi({ societaId, campi, intro }: GuidaValoriAmmessiProps) {
  const [valori,  setValori]  = useState<ValoriAmmessi | null>(null);
  const [loading, setLoading] = useState(false);
  const [errore,  setErrore]  = useState(false);
  const [aperta,  setAperta]  = useState(false);
  const caricatiRef = useRef(false);

  const carica = useCallback(async () => {
    if (!societaId || caricatiRef.current) return;
    caricatiRef.current = true;
    setLoading(true);
    setErrore(false);
    try {
      // @api GET /api/anagrafiche/articoli/valori-ammessi?societaId=N -> {"success":true,"data":"ValoriAmmessi"}
      const res = await apiFetch<{ success: boolean; data: ValoriAmmessi }>(
        `/api/anagrafiche/articoli/valori-ammessi?societaId=${societaId}`
      );
      setValori(res.data);
    } catch (err) {
      console.error('[GuidaValoriAmmessi] carica', err);
      caricatiRef.current = false; // permetti retry
      setErrore(true);
    } finally {
      setLoading(false);
    }
  }, [societaId]);

  useEffect(() => {
    if (aperta) carica();
  }, [aperta, carica]);

  // Reset cache al cambio società
  useEffect(() => {
    caricatiRef.current = false;
    setValori(null);
    setErrore(false);
  }, [societaId]);

  const listaCampi = valori ? campi(valori) : [];

  return (
    <div className="bop-card">
      <div className="bop-card__header listini-guida-header" onClick={() => setAperta(v => !v)}>
        <h2 className="bop-card__title">Guida valori ammessi</h2>
        <div className="listini-guida-header-right">
          {valori && (
            <span className="bop-badge bop-badge-warning">{listaCampi.length} campi tabellati</span>
          )}
          <span className="listini-guida-chevron">{aperta ? '▾' : '▸'}</span>
        </div>
      </div>

      {aperta && (
        <div className="bop-card__body">
          {!societaId && (
            <div className="bop-empty-state">Seleziona una società per vedere i valori disponibili</div>
          )}
          {societaId && loading && (
            <div className="bop-empty-state">Caricamento valori...</div>
          )}
          {societaId && !loading && errore && (
            <div className="bop-empty-state">
              Impossibile caricare i valori. Assicurarsi che il backend sia avviato,
              poi&nbsp;
              <button className="listini-guida-show-more" onClick={carica}>riprova</button>.
            </div>
          )}
          {societaId && !loading && valori && (
            <>
              <p className="listini-guida-intro">
                {intro ?? (
                  <>
                    Clicca su un valore per copiarlo negli appunti.
                    Inserisci nell'Excel il valore <strong>esattamente come mostrato</strong>.
                  </>
                )}
              </p>
              <div className="listini-guida-grid">
                {listaCampi.map(c => (
                  <GuidaFieldCard key={c.colonnaExcel} {...c} />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
